/**
 * widget.script — headless Script Widget (Phase L4b).
 *
 * Renders nothing at runtime; instead, on mount the widget registers a single
 * named action `run` with the layout's action registry. When a wire fires
 * `(source, topic) → (this widget id, run)`, the LayoutRenderer's wire
 * executor invokes the registered handler, which posts the snippet body into
 * the iframe sandbox (`scriptSandbox.js`) along with the wire payload + ctx.
 *
 * In Designer mode the widget renders a 1×1 placeholder so admins can see and
 * select it on the canvas. The placeholder reads the snippet's `name` so each
 * dropped script is identifiable without opening the inspector.
 *
 * Snippet body access (provided by the sandbox at invoke time):
 *   payload   // the wire payload (already piped through wire.transform_js if set)
 *   ctx       // {case, work_item, draft, current_user, role, bus, fetch, invokeAction, log}
 *   helpers   // pure utilities (date / money / template / diff / slugify / truncate)
 */
import React from 'react';
import { registerWidget } from '../widgetRegistry';
import { useLayoutCtx, useWidgetActions } from '../LayoutContext';

function ScriptWidget({ node, config }) {
  const ctx = useLayoutCtx();
  const widgetId = node?.id;

  useWidgetActions(widgetId, {
    run: async (payload) => {
      const sandbox = ctx?.scriptSandbox;
      if (!sandbox || typeof sandbox.invoke !== 'function') {
        if (typeof console !== 'undefined') console.warn(`[widget.script:${widgetId}] no sandbox available — script not run`);
        return null;
      }
      const body = (config && typeof config.body === 'string') ? config.body.trim() : '';
      if (!body) return null;
      try {
        return await sandbox.invoke('script', body, payload, ctx?.buildSandboxCtx?.({ id: widgetId, type: 'widget.script' }) || null, {
          source_widget_id: widgetId,
          timeout_ms: Number(config?.timeout_ms) > 0 ? Number(config.timeout_ms) : 5000,
        });
      } catch (err) {
        // Logged through ctx.scriptSandbox's onLog handler at the renderer level.
        if (typeof console !== 'undefined') console.warn(`[widget.script:${widgetId}]`, err?.message || err);
        return null;
      }
    },
  });

  // The Designer's Canvas surfaces script widgets via its own palette_label /
  // description rendering, so at runtime this widget is fully invisible.
  return null;
}

registerWidget({
  type: 'widget.script',
  // Available in every category — Script Widgets sit alongside data widgets in any surface.
  categories: ['home', 'case', 'task', 'add_case', 'add_task'],
  palette_label: 'Script',
  description: 'Headless JS snippet wired by topic. Runs inside an iframe sandbox; can publish events, invoke other widgets, and call out via /script-fetch.',
  Component: ScriptWidget,
  configSchema: {
    fields: [
      { type: 'text', name: 'name', label: 'Script name (shown in canvas)', default: 'untitled' },
      {
        type: 'textarea',
        editor: 'script',
        name: 'body',
        label: 'Snippet body — `payload`, `ctx`, `helpers` are in scope',
        min_height: 220,
        default: '// Example: re-broadcast the case under a custom topic\n// ctx.bus.publish("xv.MyCustomTopic", { caseId: ctx.case?.id });\n// return value is ignored for Script Widgets',
      },
      { type: 'number', name: 'timeout_ms', label: 'Timeout (ms)', default: 5000, min: 100, max: 30000 },
    ],
  },
  publishes: [],
  // Snippets can subscribe to anything via ctx.bus internally; declared subscribes
  // is intentionally empty so the per-widget event panel doesn't pre-list arbitrary topics.
  subscribes: [],
  actions: [
    { name: 'run', label: 'Run snippet', description: 'Executes the snippet body inside the iframe sandbox with the wire payload.' },
  ],
  group: 'Scripting',
});

export default ScriptWidget;
